import React from 'react';

import { useToggle } from 'react-use';
import { enqueueSnackbar } from 'notistack';
import { observer } from 'mobx-react';
import { Button, InfoBox } from '@infoods/lifebase-ui';

import classes from './CurrentPlanWidget.module.scss';
import { InitialPaymentInfo } from './InitialPaymentInfo/InitialPaymentInfo';
import { AutoRenewInfo } from './AutoRenewInfo/AutoRenewInfo';
import { openConfirmationModal } from '../../../../../shared/features/confirmation';
import { SelectReactivationSubscriptionModal } from '../SelectReactivationSubscriptionModal/SelectReactivationSubscriptionModal';
import { currentSubscriptionPlanStore } from '../../../../stores/currentSubscriptionPlanStore';
import { SubscriptionCancelAlert } from './SubscriptionCancelAlert/SubscriptionCancelAlert';
import { GeneralSubscriptionInformation } from './GeneralSubscriptionInformation/GeneralSubscriptionInformation';
import { isFuture } from '../../../../../shared/utils/dateUtils';
import { dateFormat } from '../../../../../shared/constants/apiDateFormat';
import { BillingTypeFlags } from '../../../../../shared/constants/user-info/billingType';

type CurrentPlanWidgetProps = {
  customerId: number;
  entityBillingType: BillingTypeFlags;
};

const CurrentPlanWidget = observer(
  ({ customerId, entityBillingType }: CurrentPlanWidgetProps) => {
    const [isShowSelectSubscriptionModal, toggleShowSelectSubscriptionModal] =
      useToggle(false);

    const {
      currentSubscriptionPlan,
      paymentStatusFlag,
      cancelSubscription,
      cancelSetupExplorePlan,
      setupExplorePlan,
    } = currentSubscriptionPlanStore;

    if (!currentSubscriptionPlan) {
      return null;
    }

    const { subscription, lastTransaction, explorePlan } = currentSubscriptionPlan;

    const enqueueSuccessSentEmailForSubscription = () => {
      const { customerName } = lastTransaction.billingInfo;
      const successMassage = `The payment link for the new subscription has been successfully sent to ${customerName} via email`;

      enqueueSnackbar(successMassage, { variant: 'success', autoHideDuration: 5000 });
    };

    const cancelActiveSubscription = () => {
      const title = `Are you sure you want to cancel ${subscription?.name}?`;
      const text =
        'Upon canceling, the user will still have access to the app for the time they paid for with the subscription.';

      openConfirmationModal({
        title,
        text,
        confirmBtnConfig: { text: 'Confirm', onClick: cancelSubscription },
      });
    };

    const handleCancelSubscription = () => {
      if (explorePlan) {
        cancelSetupExplorePlan();
      } else {
        cancelActiveSubscription();
      }
    };

    const handleSetupExplorePlan = async (subscriptionId: number) => {
      await setupExplorePlan(subscriptionId);

      enqueueSuccessSentEmailForSubscription();
    };

    const isHaveActivePeriod =
      lastTransaction.setupNewSubscriptionDate &&
      isFuture(lastTransaction.setupNewSubscriptionDate, dateFormat);

    const cancelledButStillActive = paymentStatusFlag.CANCELED && isHaveActivePeriod;

    const cancelledAndInactive = paymentStatusFlag.CANCELED && !isHaveActivePeriod;

    const shouldRenderCancelButton = !paymentStatusFlag.CANCELED || explorePlan;

    return (
      <>
        <InfoBox title="Current Plan">
          <div className={classes.box}>
            <div className={classes.titleRow}>
              <GeneralSubscriptionInformation />
            </div>
            <div className={classes.paymentInfoRow}>
              <InitialPaymentInfo />
              <AutoRenewInfo />
            </div>
            <div>
              {shouldRenderCancelButton && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleCancelSubscription}
                >
                  Cancel subscription
                </Button>
              )}
              {cancelledButStillActive && (
                <SubscriptionCancelAlert
                  setupNewSubscriptionDate={lastTransaction.setupNewSubscriptionDate}
                />
              )}
              {cancelledAndInactive && !explorePlan && (
                <Button
                  variant="contained"
                  size="small"
                  onClick={toggleShowSelectSubscriptionModal}
                >
                  Send Reactivation
                </Button>
              )}
            </div>
          </div>
        </InfoBox>
        {isShowSelectSubscriptionModal && (
          <SelectReactivationSubscriptionModal
            customerId={customerId}
            entityBillingType={entityBillingType}
            onClose={toggleShowSelectSubscriptionModal}
            onConfirm={handleSetupExplorePlan}
          />
        )}
      </>
    );
  },
);

export { CurrentPlanWidget };
